import React, { useEffect, useState, useRef } from "react"
import Skeleton from "react-loading-skeleton"
import Api from "@/_helpers/Api"
import { trans } from "@/_providers/Translation"
import { LineChart } from "@mui/x-charts"
import { Button } from "@mui/material"
import CompactFilterLayout from "@/components/partials/CompactFilterLayout"
import DateRange from "@/components/partials/DateRange"
import { useCalculateColor } from "@/_helpers/useCalculateColor"
import filterImage from "@/assets/img/icons/filter.svg"

const INITIAL_FILTERS = { category_id: "", date_from: null, date_to: null }

function PrevRepairTasks() {
  const [loading, setLoading] = useState(true)
  const [chartData, setChartData] = useState([])
  const [categories, setCategories] = useState([])
  const [filters, setFilters] = useState(INITIAL_FILTERS)

  const compactFilterLayoutRef = useRef(null)
  const fetchDebounceRef = useRef(null)

  const { getColorVariation } = useCalculateColor()

  useEffect(() => {
    clearTimeout(fetchDebounceRef.current)
    fetchDebounceRef.current = setTimeout(() => {
      setLoading(true)

      const f = `${filters.date_from ? `&date_from=${filters.date_from || ""}` : ""}${filters.date_to ? `&date_to=${filters.date_to || ""}` : ""}`

      Api.get(
        `analytics/past-repair-tasks?category_id=${filters.category_id}${f}`,
      )
        .then((res) => {
          if (res.data && res.data.success) {
            setChartData(res.data.data)
            setCategories(res.data.categories || [])
          }
          setLoading(false)
        })
        .catch(() => setLoading(false))
    }, 300)
    return () => clearTimeout(fetchDebounceRef.current)
  }, [filters])

  const labels = chartData.map((d) => d.label)

  function getCategoryValue(item, name) {
    const c = item.categories
    if (!c || Array.isArray(c)) return 0
    return c[name] || 0
  }

  function getSeriesNames() {
    // Prefer the order/name from the categories list, but fall back to any
    // category names that only appear inside the data.
    const names = new Set()
    categories.forEach((category) => names.add(category.name))
    chartData.forEach((item) => {
      const c = item.categories
      if (c && !Array.isArray(c)) {
        Object.keys(c).forEach((name) => names.add(name))
      }
    })
    // Keep only names that actually have data in the current range.
    return Array.from(names).filter((name) =>
      chartData.some((item) => getCategoryValue(item, name) > 0),
    )
  }

  const seriesNames = getSeriesNames()

  const series = seriesNames.map((name, index) => {
    const color = getColorVariation(index)
    return {
      id: `prtask-${index}`,
      label: name,
      data: chartData.map((item) => getCategoryValue(item, name)),
      area: true,
      color,
      showMark: false,
      curve: "catmullRom",
    }
  })

  // Per-wave gradient fill, matching the original single-series gradient.
  const areaGradientSx = {}
  series.forEach((s, index) => {
    areaGradientSx[`& .MuiAreaElement-series-${s.id}`] = {
      fill: `url(#prtask-gradient-${index})`,
    }
  })

  function getIfAnyCustomers() {
    return series.length > 0
  }

  function handleFilterChange(name, value) {
    setFilters((prev) => ({ ...prev, [name]: value }))
  }

  function getAppliedFiltersCount() {
    let count = 0
    if (filters.category_id) count++
    if (filters.date_from || filters.date_to) count++
    return count
  }

  return (
    <div className="prev-repair-tasks-chart">
      <div className="head">
        <h3>{trans("analytics.prevRepairTasks")}</h3>
        <div className="right">
          <Button
            onClick={(e) => {
              compactFilterLayoutRef.current.open(e)
            }}
          >
            <img src={filterImage} alt="Filter" />
            {getAppliedFiltersCount() ? (
              <i>{getAppliedFiltersCount()}</i>
            ) : (
              <></>
            )}
          </Button>
          <CompactFilterLayout
            ref={compactFilterLayoutRef}
            appliedFiltersCount={getAppliedFiltersCount()}
            onClearClick={() => setFilters(INITIAL_FILTERS)}
          >
            <div className="row">
              <div className="col">
                <DateRange
                  startDate={filters.date_from || ""}
                  endDate={filters.date_to || ""}
                  onStartDateChange={(date) =>
                    handleFilterChange("date_from", date)
                  }
                  onEndDateChange={(date) =>
                    handleFilterChange("date_to", date)
                  }
                  monthOnly
                />
              </div>
            </div>
            <div className="row">
              <div className="col">
                <select
                  value={filters.category_id || ""}
                  name="category_id"
                  onChange={(e) =>
                    handleFilterChange("category_id", e.target.value)
                  }
                >
                  <option value="">{trans("random.all")}</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </CompactFilterLayout>
        </div>
      </div>
      <hr />
      {loading ? (
        <Skeleton height={300} count={1} />
      ) : !getIfAnyCustomers() ? (
        <div
          className="alert warning"
          style={{
            marginBottom: 0,
            width: "100%",
          }}
        >
          {trans("messages.noDataFound")}
        </div>
      ) : (
        <LineChart
          xAxis={[
            {
              data: labels,
              scaleType: "point",
            },
          ]}
          series={series}
          height={287}
          grid={{ horizontal: true }}
          slotProps={{ legend: { hidden: true } }}
          sx={{
            ...areaGradientSx,
            "& .MuiLineElement-root": {
              strokeWidth: 1,
            },
            "& .MuiChartsGrid-line": {
              stroke: "#e0e0e0",
              strokeDasharray: "4 2",
            },
          }}
        >
          <defs>
            {series.map((s, index) => {
              const color = getColorVariation(index)
              return (
                <linearGradient
                  key={s.id}
                  id={`prtask-gradient-${index}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor={color} stopOpacity={0.25} />
                  <stop offset="100%" stopColor={color} stopOpacity={0.02} />
                </linearGradient>
              )
            })}
          </defs>
        </LineChart>
      )}
    </div>
  )
}

export default PrevRepairTasks
